import * as globals from '@wandb/weave/common/css/globals.styles';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import isUrl from 'is-url';
import {isArray, isObject} from 'lodash';
import React, {FC, useMemo} from 'react';
import {
  JSONTree as ReactJSONTree,
  KeyPath,
  ValueRenderer,
} from 'react-json-tree';

import {useViewer} from '../../state/viewer/hooks';
import {Bar} from '../../util/plotHelpers/types';
import {isUnpackedHistogramNode, UnpackedHistogram} from '../../util/runs';
import {useHideSummaryHistogramsRampFlag} from '../../util/useRampFlag';
import {formatNumber} from '../util/formatNumber';
import BarChart from '../vis/BarChart/BarChart';
import {CopyableText} from './CopyableText';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {useFilteredJSONContext} from './FilteredJSONProvider';
import {useSearchableJSONTreeSettingsContext} from './SearchableJSONTreeSettings';
import {wbJSONTreeTheme} from './util/wbJSONTreeTheme';

export const FilteredJSONTree: FC = () => {
  const filteredJSON = useFilteredJSONContext();

  const {rootKeyPath} = useSearchableJSONTreeSettingsContext();
  const analyticsRootLocation =
    rootKeyPath.length > 0 ? rootKeyPath[0] : 'unnknown';
  const valueRenderer: ValueRenderer = (valueAsString, value) => {
    const toastText = 'Copied to clipboard';
    if (typeof value === 'string' && isUrl(value)) {
      return (
        <CopyableText
          copyText={value}
          toastText={toastText}
          // eslint-disable-next-line react/no-children-prop
          children={<TargetBlank href={value}>{value}</TargetBlank>}
          rootLocation={analyticsRootLocation}
        />
      );
    }
    if (typeof value === 'number') {
      return (
        <CopyableText
          copyText={formatNumber(value)}
          // eslint-disable-next-line react/no-children-prop
          children={formatNumber(value)}
          toastText={toastText}
          rootLocation={analyticsRootLocation}
        />
      );
    }
    // TODO: move histogram display logic out of here to keep the filtered tree component more general
    //  i.e. (remove the dependency on runs)
    if (isUnpackedHistogramNode(value)) {
      return <TreeValueHistogram histogram={value} />;
    }
    if (value === null) {
      return (
        <CopyableText
          copyText={'null'}
          // eslint-disable-next-line react/no-children-prop
          children={'null'}
          toastText={toastText}
          rootLocation={analyticsRootLocation}
        />
      );
    }
    return (
      <CopyableText
        copyText={valueAsString as string}
        // eslint-disable-next-line react/no-children-prop
        children={valueAsString as string}
        toastText={toastText}
        rootLocation={analyticsRootLocation}
      />
    );
  };

  // TODO(perf): this re-renders the whole tree with each search. stop doing that if perf is an issue.
  return (
    <Tailwind>
      <div className="FilteredJSONTree">
        <ReactJSONTree
          data={filteredJSON}
          keyPath={rootKeyPath}
          theme={wbJSONTreeTheme}
          shouldExpandNodeInitially={shouldExpandNode}
          sortObjectKeys={sortObjectKeys as (a: unknown, b: unknown) => number} // TODO: type this better
          // TODO: move histogram display logic out of here to keep the filtered tree component more general
          //  i.e. (remove the dependency on runs)
          isCustomNode={isUnpackedHistogramNode}
          valueRenderer={valueRenderer}
          // invertTheme={true}
          // postprocessValue={}
          // collectionLimit={}
          // getItemString={}
          // hideRoot={}
          // labelRenderer={([key]) => <strong>{key}</strong>}
          // sortObjectKeys={}
          // valueRenderer={}
        />
      </div>
    </Tailwind>
  );
};

// TODO: move histogram display logic out of here to keep the filtered tree component more general
//  i.e. (remove the dependency on runs)
const TreeValueHistogram: FC<{histogram: UnpackedHistogram}> = ({
  histogram: {bins: histoBins, values: histoValues},
}) => {
  const dataPoints: Bar[] = useMemo(() => {
    const histogramValues = histoValues ?? [];
    const histogramBins = histoBins ?? [];
    return histogramValues.map((elem: number, index: number) => {
      const key =
        histogramBins.length > index
          ? histogramBins[index].toPrecision(4)
          : '-';
      return {
        key,
        value: elem,
        color: globals.TEAL_500,
        title: 'x= ' + key + ', y=',
      };
    });
  }, [histoBins, histoValues]);

  const viewer = useViewer();
  const hideHistogramsEnabled = useHideSummaryHistogramsRampFlag(
    viewer?.entity
  );

  if (!viewer || hideHistogramsEnabled.loading || hideHistogramsEnabled.data) {
    return (
      <div className="text-slate-400 inline italic opacity-50">histogram</div>
    );
  }

  return <BarChart bars={dataPoints} vertical={true} height={150}></BarChart>;
};

// https://stackoverflow.com/questions/2802341/natural-sort-of-alphanumerical-strings-in-javascript
const sortObjectKeys = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
}).compare;

// Auto-expand nodes with fewer than this many children
const EXPAND_CHILDREN_THRESHOLD = 5;
const shouldExpandNode = (keyPath: KeyPath, value: unknown, level: number) => {
  // console.log({keyPath, value, level});

  // Open the root node by default
  if (level === 0) {
    return true;
  }
  const childrenCount =
    (isObject(value) && Object.keys(value).length) ||
    (isArray(value) && value.length) ||
    0;

  return childrenCount < EXPAND_CHILDREN_THRESHOLD;
};
